/**
 * Partes do painel de homologação (E63.F1.C16 — extraídas de LaudoReviewPanel). // ux-neutrality:allow doc-comment
 *
 * Só apresentação: o estado da decisão e a pré-checagem ficam no painel. A // ux-neutrality:allow doc-comment
 * separação existe para o componente principal caber no limite de 200 linhas // ux-neutrality:allow doc-comment
 * do handbook depois do estado explícito do aviso prévio. // ux-neutrality:allow doc-comment
 */
import { Button, Textarea } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { CaseRecordData, ReviewDecision } from '@/lib/cases';

export function ReviewMeta({ caseRecord }: { caseRecord: CaseRecordData }): JSX.Element | null {
  const status = caseRecord.review_status ?? 'pending_review';
  if (status === 'pending_review' || !caseRecord.reviewed_by_email) return null;
  const byTemplate =
    status === 'approved' ? ptBR.laudoCaso.review.reviewedBy : ptBR.laudoCaso.review.returnedBy;
  return (
    <>
      <p className="mt-sc-2 text-body-sm text-fg-muted" data-testid="laudo-review-by">
        {byTemplate.replace('{{email}}', caseRecord.reviewed_by_email)}
      </p>
      {caseRecord.review_notes && (
        <p className="mt-1 text-body-sm text-fg" data-testid="laudo-review-notes">
          {ptBR.laudoCaso.review.notesShownLabel}: {caseRecord.review_notes}
        </p>
      )}
      {/* ADR-0068: a coincidência entre elaboração e homologação é declarada,
          nunca escondida — é ela que sustenta a decisão de não bloquear. */}
      {caseRecord.review_mesmo_autor_producao === true && (
        <p className="mt-1 text-body-sm text-fg-muted" data-testid="laudo-review-mesmo-autor">
          {ptBR.laudoCaso.review.mesmoAutorProducao}
        </p>
      )}
    </>
  );
}

export interface ReviewActionsProps {
  notes: string;
  busy: ReviewDecision | null;
  /** Pré-checagem do aviso ainda em voo: decidir agora seria decidir às cegas. */
  aguardandoAviso: boolean;
  /**
   * A pré-checagem falhou e não há valor nenhum — nem do servidor, nem aviso
   * local. Decisão bloqueada com nova tentativa explícita (F-E63.F1-09).
   */
  semInformacao: boolean;
  onNotesChange: (value: string) => void;
  onSubmit: (decision: ReviewDecision) => void;
  onRetryAviso: () => void;
}

export function ReviewActions({
  notes,
  busy,
  aguardandoAviso,
  semInformacao,
  onNotesChange,
  onSubmit,
  onRetryAviso,
}: ReviewActionsProps): JSX.Element {
  const submitting = ptBR.laudoCaso.review.submitting;
  const bloqueado = busy !== null || aguardandoAviso || semInformacao;
  return (
    <div className="mt-sc-3 flex flex-col gap-sc-2">
      <label className="text-body-sm text-fg-muted" htmlFor="laudo-review-notes-input">
        {ptBR.laudoCaso.review.notesLabel}
      </label>
      <Textarea
        id="laudo-review-notes-input"
        data-testid="laudo-review-notes-input"
        value={notes}
        onChange={(e) => onNotesChange(e.target.value)}
        placeholder={ptBR.laudoCaso.review.notesPlaceholder}
        rows={2}
      />
      <div className="flex flex-wrap gap-sc-2">
        {/* Homologar é a decisão que a tela existe para tomar: único sólido. */}
        <Button
          type="button"
          size="sm"
          onClick={() => onSubmit('approved')}
          disabled={bloqueado}
          data-testid="laudo-review-approve"
        >
          {busy === 'approved' ? submitting : ptBR.laudoCaso.review.approveCta}
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => onSubmit('rejected')}
          disabled={bloqueado}
          data-testid="laudo-review-reject"
        >
          {busy === 'rejected' ? submitting : ptBR.laudoCaso.review.rejectCta}
        </Button>
      </div>
      {aguardandoAviso && (
        <p className="text-body-sm text-fg-muted" data-testid="laudo-review-hint-loading">
          {ptBR.laudoCaso.review.selfReviewChecking}
        </p>
      )}
      {semInformacao && (
        <div className="flex flex-wrap items-center gap-sc-2" data-testid="laudo-review-hint-error">
          <p role="alert" className="m-0 text-body-sm text-fg">
            {ptBR.laudoCaso.review.selfReviewUnavailable}
          </p>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onRetryAviso}
            data-testid="laudo-review-hint-retry"
          >
            {ptBR.laudoCaso.review.selfReviewRetryCta}
          </Button>
        </div>
      )}
    </div>
  );
}
